import React from 'react'
import { Card, CardContent, Typography, Box, useTheme } from '@mui/material'

interface VendorUnitAreaPricingCardProps {
    name: string
    currentPrice: number | null
    vendorPrice: number | null
}

const renderPriceDiff = (
    currentPrice: number | null,
    vendorPrice: number | null,
) => {
    if (
        typeof currentPrice === 'number' &&
        typeof vendorPrice === 'number' &&
        currentPrice !== 0
    ) {
        const diff = vendorPrice - currentPrice
        const percent = (diff / currentPrice) * 100
        if (diff === 0) {
            return (
                <Typography
                    component="span"
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 400,
                        fontSize: 13,
                    }}
                >
                    No change
                </Typography>
            )
        }
        const isIncrease = diff > 0
        const color = isIncrease ? 'success.main' : 'error.main'
        const arrow = isIncrease ? '▲' : '▼'
        return (
            <Typography
                component="span"
                sx={{ color, fontWeight: 500, fontSize: 13 }}
            >
                {`${arrow} $${Math.abs(diff).toFixed(2)} (${arrow} ${Math.abs(
                    percent,
                ).toFixed(0)}%)`}
            </Typography>
        )
    }
    return null
}

export const VendorUnitAreaPricingCard: React.FC<VendorUnitAreaPricingCardProps> = ({
    name,
    currentPrice,
    vendorPrice,
}) => {
    const theme = useTheme()
    // Calculate if we should show the diff
    const showDiff =
        typeof currentPrice === 'number' &&
        typeof vendorPrice === 'number' &&
        currentPrice !== 0
    // Reserve space for the change details line
    const CHANGE_LINE_HEIGHT = 22 // px, adjust as needed for your font size
    return (
        <Card
            sx={{
                minWidth: 220,
                mb: 2,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: 'none',
                borderRadius: 2,
                background: theme.palette.background.paper,
            }}
        >
            <CardContent sx={{ pb: '16px !important', pt: 2 }}>
                <Typography fontWeight={700} fontSize={18} mb={0.5}>
                    {name}
                </Typography>
                <Box
                    mb={2}
                    sx={{
                        minHeight: CHANGE_LINE_HEIGHT,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    {showDiff
                        ? renderPriceDiff(currentPrice, vendorPrice)
                        : null}
                </Box>
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="stretch"
                    gap={0.5}
                    sx={{ marginRight: theme.spacing(4) }}
                >
                    {(typeof currentPrice === 'number' ||
                        typeof vendorPrice === 'number') && (
                        <>
                            <Box
                                display="flex"
                                flexDirection="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ minWidth: '110px' }}
                            >
                                <Typography
                                    color="text.secondary"
                                    fontSize={14}
                                    sx={{ textAlign: 'left', flex: 1 }}
                                >
                                    Current:
                                </Typography>
                                <Typography
                                    color="text.secondary"
                                    fontSize={14}
                                    sx={{ textAlign: 'right', flex: 1 }}
                                >
                                    {typeof currentPrice === 'number'
                                        ? `$${currentPrice.toFixed(2)}`
                                        : '—'}
                                </Typography>
                            </Box>
                            <Box
                                display="flex"
                                flexDirection="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ minWidth: '110px' }}
                            >
                                <Typography
                                    color="primary"
                                    fontWeight="bold"
                                    fontSize={14}
                                    sx={{ textAlign: 'left', flex: 1 }}
                                >
                                    New:
                                </Typography>
                                <Typography
                                    color="primary"
                                    fontWeight="bold"
                                    fontSize={14}
                                    sx={{ textAlign: 'right', flex: 1 }}
                                >
                                    {typeof vendorPrice === 'number'
                                        ? `$${vendorPrice.toFixed(2)}`
                                        : '—'}
                                </Typography>
                            </Box>
                        </>
                    )}
                </Box>
            </CardContent>
        </Card>
    )
}
